import { addDays } from 'date-fns';
import type { Appointment } from '@/api/appointments/appointmentsApi.types';
import { APPOINTMENT_STATUS_CODE } from '@/constants/appointmentOptions';
import type { VisitQuickAction } from '@/components/practitioner/shared/VisitQuickActions';
import { formatDateToIsoDateOnly } from '@/utils/dateFormatting';

const CLOCK_FORMAT_LENGTH = 5;

export const UPCOMING_VISIT_DAYS = 7;

const HIDDEN_STATUSES: readonly string[] = [
  APPOINTMENT_STATUS_CODE.cancelled,
  APPOINTMENT_STATUS_CODE.noShow,
];

const OPEN_STATUSES: readonly string[] = [
  APPOINTMENT_STATUS_CODE.planned,
  APPOINTMENT_STATUS_CODE.confirmed,
  APPOINTMENT_STATUS_CODE.inPreparation,
];

export interface UpcomingVisitDay {
  date: string;
  visits: Appointment[];
}

export function groupUpcomingVisits(appointments: Appointment[], today: Date): UpcomingVisitDay[] {
  const firstDate = formatDateToIsoDateOnly(today);
  const lastDate = formatDateToIsoDateOnly(addDays(today, UPCOMING_VISIT_DAYS));
  const visibleVisits = appointments
    .filter(function keepUpcoming(appointment) {
      return (
        appointment.date >= firstDate &&
        appointment.date <= lastDate &&
        !HIDDEN_STATUSES.includes(appointment.status)
      );
    })
    .sort(function byDateAndTime(first, second) {
      return (first.date + first.startTime).localeCompare(second.date + second.startTime);
    });
  const days: UpcomingVisitDay[] = [];
  for (const visit of visibleVisits) {
    const lastDay = days[days.length - 1];
    if (lastDay !== undefined && lastDay.date === visit.date) {
      lastDay.visits.push(visit);
    } else {
      days.push({ date: visit.date, visits: [visit] });
    }
  }
  return days;
}

export function quickActionsForVisit(visit: Appointment, now: Date): VisitQuickAction[] {
  if (!OPEN_STATUSES.includes(visit.status)) {
    return [];
  }
  const nowClock = now.toTimeString().slice(0, CLOCK_FORMAT_LENGTH);
  if (visit.date === formatDateToIsoDateOnly(now) && visit.startTime <= nowClock) {
    return ['writeNote', 'noShow'];
  }
  if (visit.status === APPOINTMENT_STATUS_CODE.planned) {
    return ['confirm', 'cancel'];
  }
  return ['cancel'];
}
